import styled, { css } from 'styled-components';
import colors from '../theme/colors';
import fonts from '../theme/fonts';
import { getUserThemeContrastColor } from '../theme/colors';

// Base typography styles shared across components
const baseTypographyStyles = css`
  margin: 0 0 ${(props) => props.$marginBottom || '0'} 0;
  color: ${(props) => props.color || getUserThemeContrastColor(props.$themeColor)};
  text-align: ${(props) => props.$textAlign || 'inherit'};
  text-transform: ${(props) => (props.$caps ? 'uppercase' : 'none')};
  font-style: ${(props) => (props.$italic ? 'italic' : 'normal')};
`;

// Base heading styles
const headingStyles = css`
  ${baseTypographyStyles}
  font-family: ${fonts.HEADING_FONT}, sans-serif;
  font-weight: 600;
`;

// Base text styles
const textStyles = css`
  ${baseTypographyStyles}
  font-family: ${fonts.TEXT_FONT}, sans-serif;
  font-weight: ${(props) => props.fontWeight || '300'};
  white-space: ${(props) => props.$whiteSpace || 'normal'};
  overflow-wrap: ${(props) => props.$overflowWrap || 'anywhere'};

  a,
  a:hover,
  a:visited {
    font-weight: 400;
    cursor: pointer;
    color: ${colors.PRIMARY};
  }
`;

/**
 * Typography components for headings and text
 */
const Typo = {
  // HEADING COMPONENTS

  /**
   * Page Header - H1 element for main page titles
   */
  PageHeader: styled.h1`
    ${headingStyles}
    line-height: 40px;
    font-size: ${fonts.fontSizes.h1};
    text-align: ${(props) => props.$textAlign || 'center'};
  `,

  /**
   * Section Header - H2 element for section titles
   */
  SectionHeader: styled.h2`
    ${headingStyles}
    line-height: 30px;
    font-size: ${fonts.fontSizes.h2};
    text-align: ${(props) => props.$textAlign || 'center'};
  `,

  /**
   * Content Big Header - H3 element to be used with only one item
   */
  ContentBigHeader: styled.h3`
    ${headingStyles}
    line-height: 30px;
    font-size: ${fonts.fontSizes.h3};
    text-align: ${(props) => props.$textAlign || 'center'};
  `,

  /**
   * Content Small Header - H4 element to be used with multiple items
   */
  ContentSmallHeader: styled.h4`
    ${headingStyles}
    line-height: 30px;
    font-size: ${fonts.fontSizes.h4};
    text-align: ${(props) => props.$textAlign || 'center'};
  `,

  /**
   * Text Title - Paragraph that looks like a heading
   */
  TextTitle: styled.p`
    ${headingStyles}
    line-height: 24px;
    font-size: ${fonts.fontSizes.medium};
    text-align: ${(props) => props.$textAlign || 'left'};
    white-space: ${(props) => props.whiteSpace};
  `,

  // TEXT COMPONENTS

  /**
   * Super Hero - Very large text for hero sections
   */
  SuperHero: styled.p`
    ${textStyles}
    font-weight: ${(props) => props.fontWeight || '400'};
    width: 100%;
    font-size: 50px;
    text-align: ${(props) => props.$textAlign || 'left'};
    white-space: pre-wrap;
  `,

  /**
   * Big Text - Large text for important content
   */
  BigText: styled.p`
    ${textStyles}
    line-height: 34px;
    font-size: ${fonts.fontSizes.h3};
    text-align: ${(props) => props.$textAlign || 'left'};
  `,

  /**
   * Text - Standard paragraph text
   */
  Text: styled.p`
    ${textStyles}
    line-height: 28px;
    width: 100%;
    font-size: ${fonts.fontSizes.regular}; //18px
    text-align: ${(props) => props.$textAlign || 'left'};
    white-space: pre-wrap;
  `,

  /**
   * Medium Text - Slightly smaller than standard text
   */
  MediumText: styled.p`
    ${textStyles}
    line-height: 22px;
    font-size: ${fonts.fontSizes.medium}; // 16px
    text-align: ${(props) => props.$textAlign || 'left'};
  `,

  /**
   * Small Text - Small text for less important content
   */
  SmallText: styled.p`
    ${textStyles}
    line-height: 22px;
    font-size: ${fonts.fontSizes.small};
    text-align: ${(props) => props.$textAlign || 'left'};
  `,

  /**
   * Info - Extra small text for supplementary information
   */
  Info: styled.p`
    ${textStyles}
    font-weight: ${(props) => props.fontWeight || '400'};
    line-height: 18px;
    font-size: ${fonts.fontSizes.xSmall};
    text-align: ${(props) => props.$textAlign || 'left'};
  `,

  /**
   * FigCaption - For image captions
   */
  FigCaption: styled.figcaption`
    ${textStyles}
    font-size: ${fonts.fontSizes.xSmall};
    margin-top: 10px;
  `,

  /**
   * Wrapper - A div with text styling
   */
  Wrapper: styled.div`
    font-family: ${fonts.TEXT_FONT}, sans-serif;
    color: ${(props) => props.color || getUserThemeContrastColor(props.$themeColor)};
    text-align: ${(props) => props.$textAlign || 'left'};
  `,
};

export default Typo;
